/**
 * `<plp-slider>` — лёгкая карусель без зависимостей (~3KB).
 *
 * Каждый прямой потомок становится слайдом. Поддерживает:
 *   - стрелки (`arrows`)
 *   - точки (`dots`)
 *   - autoplay (`autoplay="ms"`, в edit-mode на паузе)
 *   - loop (`loop`)
 *   - touch swipe + клавиатура
 *   - slides-per-view (responsive: на мобильных всегда 1)
 *   - gap между слайдами
 */

import { isEditMode, onEditModeChange } from '../core/edit-mode.js';
import { t } from '../i18n/index.js';

export class PlpSlider extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['autoplay', 'arrows', 'dots', 'loop', 'slides-per-view', 'gap'];
  }

  private _index = 0;
  private _slides: HTMLElement[] = [];
  private _viewport!: HTMLElement;
  private _track!: HTMLElement;
  private _dots?: HTMLElement;
  private _autoplayTimer?: number;
  private _editUnsubscribe?: () => void;
  private _initialized = false;

  connectedCallback(): void {
    if (!this._initialized) {
      this.buildShell();
      this._initialized = true;
    }
    this.layout();
    this.bindEvents();
    this._editUnsubscribe = onEditModeChange(() => this.refreshAutoplay());
  }

  disconnectedCallback(): void {
    this.stopAutoplay();
    this._editUnsubscribe?.();
  }

  attributeChangedCallback(): void {
    if (!this.isConnected) return;
    this.layout();
    this.refreshAutoplay();
  }

  private buildShell(): void {
    // Сохраняем оригинальные дочерние элементы, оборачиваем их в track.
    const originals = Array.from(this.children) as HTMLElement[];
    this._slides = originals;

    this.style.display = 'block';
    this.style.position = 'relative';
    this.style.overflow = 'hidden';

    const viewport = document.createElement('div');
    viewport.className = 'plp-slider-viewport';
    viewport.style.overflow = 'hidden';
    viewport.style.position = 'relative';
    viewport.style.width = '100%';

    const track = document.createElement('div');
    track.className = 'plp-slider-track';
    track.style.display = 'flex';
    track.style.transition = 'transform 0.4s ease';
    track.style.willChange = 'transform';

    for (const slide of this._slides) {
      slide.classList.add('plp-slider-slide');
      slide.style.flex = '0 0 auto';
      const img = slide.querySelector?.('img');
      img?.setAttribute('loading', 'lazy');
      track.appendChild(slide);
    }
    viewport.appendChild(track);
    this.innerHTML = '';
    this.appendChild(viewport);
    this._viewport = viewport;
    this._track = track;

    this.tabIndex = 0;
  }

  private layout(): void {
    const perView = this.slidesPerView();
    const gap = this.gap();
    const slideWidth = `calc((100% - ${(perView - 1) * gap}px) / ${perView})`;
    for (const slide of this._slides) {
      slide.style.width = slideWidth;
      slide.style.marginRight = `${gap}px`;
    }
    if (this._slides.length > 0) {
      this._slides[this._slides.length - 1].style.marginRight = '0';
    }
    this.renderControls();
    this.go(this._index, false);
  }

  private bindEvents(): void {
    this.addEventListener('keydown', this.onKeyDown);
    let startX = 0;
    let dx = 0;
    let dragging = false;
    this._viewport.addEventListener('pointerdown', (e) => {
      dragging = true;
      startX = e.clientX;
      dx = 0;
      this._viewport.setPointerCapture(e.pointerId);
      this._track.style.transition = 'none';
    });
    this._viewport.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      dx = e.clientX - startX;
      const offset = this.currentOffset() + dx;
      this._track.style.transform = `translateX(${offset}px)`;
    });
    const finish = (): void => {
      if (!dragging) return;
      dragging = false;
      this._track.style.transition = '';
      const threshold = this._viewport.clientWidth / 5;
      if (dx > threshold) this.prev();
      else if (dx < -threshold) this.next();
      else this.go(this._index, true);
      dx = 0;
    };
    this._viewport.addEventListener('pointerup', finish);
    this._viewport.addEventListener('pointercancel', finish);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    }
  };

  private renderControls(): void {
    this.querySelectorAll(':scope > .plp-slider-arrow, :scope > .plp-slider-dots').forEach((n) =>
      n.remove()
    );
    if (this.hasAttribute('arrows')) {
      const prev = this.makeArrow('prev', '‹');
      const next = this.makeArrow('next', '›');
      prev.addEventListener('click', () => this.prev());
      next.addEventListener('click', () => this.next());
      this.appendChild(prev);
      this.appendChild(next);
    }
    if (this.hasAttribute('dots')) {
      const dots = document.createElement('div');
      dots.className = 'plp-slider-dots';
      Object.assign(dots.style, {
        position: 'absolute',
        bottom: '12px',
        left: '0',
        right: '0',
        display: 'flex',
        gap: '6px',
        justifyContent: 'center',
        zIndex: '2',
      });
      const slideCount = this._slides.length;
      for (let i = 0; i < slideCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'plp-slider-dot';
        Object.assign(dot.style, {
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          border: 'none',
          background: i === this._index ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)',
          cursor: 'pointer',
          padding: '0',
        });
        dot.addEventListener('click', () => this.go(i, true));
        dots.appendChild(dot);
      }
      this.appendChild(dots);
      this._dots = dots;
    }
  }

  private makeArrow(dir: 'prev' | 'next', symbol: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `plp-slider-arrow plp-slider-arrow--${dir}`;
    btn.setAttribute('aria-label', dir === 'prev' ? t('slider.previous') : t('slider.next'));
    btn.textContent = symbol;
    Object.assign(btn.style, {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      [dir === 'prev' ? 'left' : 'right']: '8px',
      width: '36px',
      height: '36px',
      borderRadius: '9999px',
      border: 'none',
      background: 'rgba(0,0,0,0.55)',
      color: 'white',
      fontSize: '24px',
      lineHeight: '1',
      cursor: 'pointer',
      zIndex: '2',
    });
    return btn;
  }

  private currentOffset(): number {
    if (this._slides.length === 0) return 0;
    const slideWidth = this._slides[0].getBoundingClientRect().width;
    const gap = this.gap();
    return -this._index * (slideWidth + gap);
  }

  private go(index: number, animate = true): void {
    const max = this._slides.length - 1;
    if (max < 0) return;
    if (index < 0) index = this.hasAttribute('loop') ? max : 0;
    else if (index > max) index = this.hasAttribute('loop') ? 0 : max;
    this._index = index;
    this._track.style.transition = animate ? '' : 'none';
    this._track.style.transform = `translateX(${this.currentOffset()}px)`;
    if (!animate) {
      // forced reflow → восстанавливаем transition на следующий tick
      void this._track.offsetWidth;
      this._track.style.transition = '';
    }
    this.updateDots();
  }

  private updateDots(): void {
    if (!this._dots) return;
    Array.from(this._dots.children).forEach((child, i) => {
      (child as HTMLElement).style.background =
        i === this._index ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)';
    });
  }

  prev(): void {
    this.go(this._index - 1);
  }
  next(): void {
    this.go(this._index + 1);
  }

  private slidesPerView(): number {
    const raw = this.getAttribute('slides-per-view');
    const n = raw ? parseInt(raw, 10) : 1;
    if (window.matchMedia('(max-width: 640px)').matches) return 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  private gap(): number {
    const n = parseInt(this.getAttribute('gap') ?? '0', 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  private autoplayMs(): number {
    const raw = this.getAttribute('autoplay');
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  private refreshAutoplay(): void {
    this.stopAutoplay();
    if (this.autoplayMs() > 0 && !isEditMode()) {
      this._autoplayTimer = window.setInterval(() => this.next(), this.autoplayMs());
    }
  }

  private stopAutoplay(): void {
    if (this._autoplayTimer !== undefined) {
      window.clearInterval(this._autoplayTimer);
      this._autoplayTimer = undefined;
    }
  }
}
